"use client";

import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { blogBodyToHTML } from "@/lib/blog-markdown";

function sanitizeHTML(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "b", "i", "strong", "em", "del", "s", "ul", "ol", "li", "a", "h1", "h2", "h3", "h4", "h5", "h6", "span", "div", "blockquote", "code", "pre", "img", "hr"],
    ALLOWED_ATTR: ["href", "target", "rel", "class", "src", "alt"],
    ALLOW_DATA_ATTR: false,
  });
}

// Standard email shape. Non-global copy is used for tests so lastIndex never leaks.
const EMAIL_TEST = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const EMAIL_GLOBAL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

const COPY_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
const CHECK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

function makeCopyButton(email: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", "Copy email");
  btn.title = "Copy email";
  btn.className =
    "not-prose inline-flex size-5 items-center justify-center rounded align-middle text-text-quaternary transition-colors hover:text-text-primary cursor-pointer";
  btn.innerHTML = COPY_SVG;

  let revert: ReturnType<typeof setTimeout> | undefined;
  btn.addEventListener("click", async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      // Clipboard may be blocked; keep the address visible so it can be copied manually.
      return;
    }
    btn.innerHTML = CHECK_SVG;
    btn.setAttribute("aria-label", "Copied");
    btn.title = "Copied";
    if (revert) clearTimeout(revert);
    revert = setTimeout(() => {
      btn.innerHTML = COPY_SVG;
      btn.setAttribute("aria-label", "Copy email");
      btn.title = "Copy email";
    }, 1500);
  });
  return btn;
}

// Walks the rendered content and turns every email into plain text plus a copy
// button. Runs at render time so existing posts get the treatment with no
// content migration. Emails inside code/pre or intentional non-mailto links are
// left untouched.
function enhanceEmails(root: HTMLElement) {
  // Unwrap mailto anchors so the address renders as plain (non-underlined) text.
  root.querySelectorAll('a[href^="mailto:" i]').forEach((a) => {
    a.replaceWith(document.createTextNode(a.textContent || ""));
  });

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !EMAIL_TEST.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
      let el = node.parentElement;
      while (el && el !== root) {
        const tag = el.tagName;
        if (tag === "CODE" || tag === "PRE" || tag === "A" || tag === "BUTTON") return NodeFilter.FILTER_REJECT;
        el = el.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const targets: Text[] = [];
  let current: Node | null;
  while ((current = walker.nextNode())) targets.push(current as Text);

  targets.forEach((textNode) => {
    const value = textNode.nodeValue ?? "";
    EMAIL_GLOBAL.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = EMAIL_GLOBAL.exec(value))) {
      const before = value.slice(last, match.index);
      if (before) frag.appendChild(document.createTextNode(before));

      const email = match[0];
      const wrapper = document.createElement("span");
      wrapper.className = "inline-flex items-center gap-1 whitespace-nowrap";
      wrapper.appendChild(document.createTextNode(email));
      wrapper.appendChild(makeCopyButton(email));
      frag.appendChild(wrapper);

      last = match.index + email.length;
    }
    const after = value.slice(last);
    if (after) frag.appendChild(document.createTextNode(after));
    textNode.replaceWith(frag);
  });
}

// `content` is the stored body: Markdown, or HTML for posts written before the
// Markdown switch. Both become the same markup (see blogBodyToHTML), so the
// caller's typography classes style either one identically.
export function BlogContent({ content, className }: { content: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const safe = sanitizeHTML(blogBodyToHTML(content));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // React owns this node's markup via dangerouslySetInnerHTML; re-applying the
    // sanitized source first keeps the transform idempotent across re-renders.
    el.innerHTML = safe;
    enhanceEmails(el);
  }, [safe]);

  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: safe }} />;
}
